/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useCallback } from "react";
import { api, spriteUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Map, Lock, MapPin, Package, ChevronRight, Send, X } from "lucide-react";

const DIFFICULTY_LABEL = (d) =>
  d <= 2 ? "Facile" : d <= 5 ? "Moyenne" : d <= 8 ? "Difficile" : "Élite";

const REGION_LABEL = { kanto: "Kanto", johto: "Johto" };

export default function TravelJournalPage() {
  const [routes, setRoutes] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [region, setRegion] = useState("kanto");
  const [send, setSend] = useState(null); // {action, route}

  const load = useCallback(async () => {
    try {
      const [j, t] = await Promise.all([
        api.get("/journal/routes"),
        api.get("/trainers"),
      ]);
      setRoutes(j.data.routes);
      setTrainers(t.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Regions available in the returned routes (backend already filters by unlocks).
  const regions = routes
    ? Array.from(new Set(routes.map((r) => r.region || "kanto")))
    : ["kanto"];

  // Ensure current region is always in the list (fallback to first available).
  useEffect(() => {
    if (routes && regions.length && !regions.includes(region)) {
      setRegion(regions[0]);
    }
  }, [routes, regions, region]);

  const routesInRegion = (routes || []).filter(
    (r) => (r.region || "kanto") === region,
  );

  // Auto-select the first route of the current region whenever it changes.
  useEffect(() => {
    if (!routesInRegion.length) { setSelected(null); return; }
    if (!routesInRegion.some((r) => r.id === selected)) {
      setSelected(routesInRegion[0].id);
    }
  }, [region, routesInRegion, selected]);

  if (!routes) {
    return <div className="font-mono text-xs text-[#7A7A7A]">Chargement du carnet…</div>;
  }

  const current = routesInRegion.find((r) => r.id === selected) || routesInRegion[0];

  return (
    <div data-testid="travel-journal-page">
      <div className="mb-4">
        <div className="overline">Carnet de voyage · {routesInRegion.length} routes · {REGION_LABEL[region] || region}</div>
        <h1 className="font-serif-display text-4xl md:text-5xl leading-none mt-1">Carnet</h1>
        <p className="text-xs text-[#4A4A4A] mt-2 max-w-2xl">
          Toutes les routes explorées par vos dresseurs : les Pokémon aperçus,
          ceux capturés, et les objets ramenés. Envoyez un dresseur en mission ciblée
          pour compléter votre Pokédex ou récupérer un objet rare.
        </p>
        {regions.length > 1 && (
          <div className="flex items-center gap-1 mt-3" data-testid="region-tabs">
            <span className="overline mr-1">Région :</span>
            {regions.map((r) => (
              <button
                key={r}
                data-testid={`region-tab-${r}`}
                onClick={() => setRegion(r)}
                className={`btn-ink text-[10px] py-1 px-2 ${region === r ? "btn-leaf" : ""}`}
                aria-pressed={region === r}
              >
                {REGION_LABEL[r] || r}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Routes list */}
        <aside className="col-span-12 lg:col-span-4 panel p-2 max-h-[640px] overflow-auto" data-testid="route-list">
          {routesInRegion.length === 0 && (
            <div className="p-3 text-xs font-mono text-[#7A7A7A]">
              Aucune route disponible pour {REGION_LABEL[region] || region}.
            </div>
          )}
          {routesInRegion.map((r) => {
            const caught = r.pokemon.filter((p) => p.status === "caught").length;
            const total = r.pokemon.length;
            const isSel = r.id === current?.id;
            return (
              <button
                key={r.id}
                data-testid={`route-${r.id}`}
                onClick={() => setSelected(r.id)}
                className={`w-full text-left p-2 mb-1 border ${isSel ? "border-[#B87333] bg-[#FCF9F2]" : "border-transparent hover:bg-[#FCF9F2]"} flex items-center gap-2`}
              >
                <MapPin size={14} className="shrink-0 text-[#2E4F32]"/>
                <div className="flex-1 min-w-0">
                  <div className="font-serif-display text-base leading-tight truncate">{r.name}</div>
                  <div className="font-mono text-[10px] text-[#4A4A4A]">
                    Diff {r.difficulty} · {DIFFICULTY_LABEL(r.difficulty)} · {caught}/{total} capturés
                  </div>
                </div>
                {r.min_badges > 0 && (
                  <span className="font-mono text-[9px] text-[#7A7A7A] border border-[#7A7A7A] px-1">
                    {r.min_badges}🏅
                  </span>
                )}
                <ChevronRight size={12} className="text-[#7A7A7A] shrink-0"/>
              </button>
            );
          })}
        </aside>

        {/* Route detail */}
        <section className="col-span-12 lg:col-span-8 panel p-4" data-testid="route-detail">
          {current && (
            <>
              <div className="flex items-start justify-between gap-3 border-b border-[#2B2B2B] pb-3 mb-3">
                <div>
                  <div className="overline">Route {REGION_LABEL[current.region || "kanto"] || current.region}</div>
                  <h2 className="font-serif-display text-3xl leading-none mt-1">{current.name}</h2>
                  <p className="text-[11px] font-mono text-[#4A4A4A] mt-1">
                    Difficulté <strong>{current.difficulty}</strong> · {DIFFICULTY_LABEL(current.difficulty)} ·
                    Accès : {current.min_badges} badge(s) requis
                  </p>
                </div>
                <div className="flex gap-1 flex-wrap justify-end">
                  <button
                    data-testid="send-explore"
                    className="btn-ink text-[10px] py-1 px-2"
                    onClick={() => setSend({ action: "explore", route: current })}
                  >
                    <Send size={10}/> Explorer
                  </button>
                  <button
                    data-testid="send-capture"
                    className="btn-ink text-[10px] py-1 px-2"
                    onClick={() => setSend({ action: "capture", route: current })}
                  >
                    <Send size={10}/> Capturer
                  </button>
                  <button
                    data-testid="send-train"
                    className="btn-ink text-[10px] py-1 px-2"
                    onClick={() => setSend({ action: "train", route: current })}
                  >
                    <Send size={10}/> Entraîner
                  </button>
                </div>
              </div>

              {/* Visit stats */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                <Stat label="Explorations" value={current.visits.explores}/>
                <Stat label="Captures" value={current.visits.captures}/>
                <Stat label="Échecs capture" value={current.visits.misses}/>
                <Stat label="Espèces aperçues" value={current.pokemon.filter((p) => p.status !== "undiscovered").length}/>
              </div>

              {/* Pokémon known */}
              <div className="overline mb-2">Pokémon répertoriés ici</div>
              {current.pokemon.length === 0 ? (
                <div className="text-xs font-mono text-[#7A7A7A] mb-4">Aucune espèce connue sur cette route.</div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4">
                  {current.pokemon.map((p) => {
                    const status = p.status; // caught | seen | undiscovered
                    const unseen = status === "undiscovered";
                    return (
                      <div
                        key={p.pokemon_id}
                        data-testid={`route-pkmn-${p.pokemon_id}`}
                        className={`panel p-2 text-center ${unseen ? "opacity-80" : ""}`}
                        title={unseen ? "Pas encore aperçu" : status === "seen" ? "Aperçu" : "Capturé"}
                      >
                        {unseen ? (
                          <div className="w-12 h-12 mx-auto flex items-center justify-center bg-[#EAE4D9] border border-[#2B2B2B] font-serif-display text-2xl text-[#7A7A7A]">
                            ?
                          </div>
                        ) : (
                          <img
                            src={spriteUrl(p.pokemon_id)}
                            alt={p.name}
                            className="pixelated w-12 h-12 mx-auto"
                          />
                        )}
                        <div className="font-mono text-[10px] mt-1 truncate">
                          {unseen ? "???" : p.name}
                        </div>
                        <div className="overline text-[8px] mt-0.5">
                          {status === "caught" ? "✓ capturé" : status === "seen" ? "Aperçu" : "—"}
                          {p.is_shiny ? " ✨" : ""}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Items found */}
              <div className="overline mb-2 flex items-center gap-2">
                <Package size={11}/> Objets ramenés
              </div>
              {current.items_found.length === 0 ? (
                <div className="text-xs font-mono text-[#7A7A7A]">
                  Aucun objet trouvé ici. Envoyez un dresseur explorateur pour fouiller.
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {current.items_found.map((it) => (
                    <div key={it.item_id} className="panel p-2 flex items-center gap-2">
                      <img src={it.sprite} alt={it.name} className="pixelated w-8 h-8 shrink-0"/>
                      <div className="flex-1 min-w-0">
                        <div className="font-serif-display text-sm leading-none truncate">{it.name}</div>
                        <div className="font-mono text-[10px] text-[#4A4A4A]">×{it.found_count} · {it.type}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {/* Trainer picker for sending action */}
      {send && (
        <SendModal
          trainers={trainers}
          action={send.action}
          route={send.route}
          onClose={() => setSend(null)}
          onSent={() => { setSend(null); toast.success("Mission envoyée"); load(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="panel p-2 text-center">
      <div className="overline text-[9px]">{label}</div>
      <div className="font-serif-display text-2xl leading-none">{value ?? 0}</div>
    </div>
  );
}

function SendModal({ trainers, action, route, onClose, onSent }) {
  const [busy, setBusy] = useState(false);
  const actionLabel = { explore: "Exploration", capture: "Capture", train: "Entraînement" }[action];

  const submit = async (trainerId) => {
    setBusy(true);
    try {
      await api.post(`/trainers/${trainerId}/strategy`, { strategy: action, target: route.id });
      onSent();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  // Filter trainers that can access this route
  const eligible = trainers.filter((t) => (t.badges?.length || 0) >= route.min_badges);
  const blocked = trainers.length - eligible.length;

  return (
    <div className="fixed inset-0 z-50 bg-[#1A1A1A]/70 flex items-center justify-center p-4 overflow-auto" onClick={onClose}>
      <div className="bg-[#F4EFE6] border-2 border-[#1A1A1A] paper-noise w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-[#2B2B2B] p-4 flex items-start justify-between">
          <div>
            <div className="overline">Envoyer en mission</div>
            <h2 className="font-serif-display text-2xl leading-tight">{actionLabel} sur {route.name}</h2>
            <p className="text-[11px] font-mono text-[#4A4A4A] mt-1">
              Sélectionnez un dresseur. Le forçage est ponctuel, il reprendra ensuite son auto.
            </p>
          </div>
          <button className="btn-ink p-1.5" onClick={onClose}><X size={14}/></button>
        </div>
        <div className="p-4">
          {eligible.length === 0 ? (
            <div className="text-xs font-mono text-[#7A7A7A]">
              <Lock size={11} className="inline mr-1"/>
              Aucun dresseur ne dispose des {route.min_badges} badge(s) requis pour cette route.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {eligible.map((t) => (
                <button
                  key={t.id}
                  data-testid={`send-pick-${t.id}`}
                  disabled={busy}
                  onClick={() => submit(t.id)}
                  className="panel p-2 text-left hover:bg-[#FCF9F2] flex items-center gap-3"
                >
                  {t.starter_pokemon_id && (
                    <img src={spriteUrl(t.starter_pokemon_id)} alt="" className="pixelated w-10 h-10 shrink-0"/>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-serif-display text-base leading-tight truncate">{t.name}</div>
                    <div className="font-mono text-[10px] text-[#4A4A4A]">
                      Niv {t.level} · {t.badges?.length || 0} badge(s) · {t.current_action}
                    </div>
                  </div>
                  <ChevronRight size={14} className="text-[#7A7A7A]"/>
                </button>
              ))}
            </div>
          )}
          {blocked > 0 && (
            <p className="text-[10px] font-mono text-[#7A7A7A] mt-3 flex items-center gap-1">
              <Lock size={10}/> {blocked} dresseur(s) ne peuvent pas accéder à cette route.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
